import type { SelectOption } from '@/features/editMode/types/selectOption';
import { DocumentEntityOptionsProps, DocumentTypeOption } from '@/services/DocumentCreationService/types';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsSelectPropertyNames } from '@/features/eds/enums/EdsSelectPropertyNames';
import { DocumentEntityType } from '@/domains/documents/enums/DocumentEntityType';
import HttpService from '@/services/HttpService/HttpService';
import TypographyService from '@/services/TypographyService/TypographyService';

export class DocumentCreationService {
  private documentTypeOptions: DocumentTypeOption[];

  constructor() {
    this.documentTypeOptions = [];
  }

  public static async createServiceInstance() {
    const service = new DocumentCreationService();

    const responseOptions = await HttpService.get<DocumentTypeOption[]>(
      `/options/${EdsEntityCodes.Document}/${EdsSelectPropertyNames.DocumentTypeId}`,
      undefined,
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    service.documentTypeOptions = responseOptions.data[EdsSelectPropertyNames.DocumentTypeId].map((option) => ({
      ...option,
      metadata: {
        ...option.metadata,
        // convert entity name like `EntityName` to `entityName` to be compatible with DocumentEntityType
        entityLevelString: TypographyService.camelCase(
          TypographyService.splitByCapitalLetters(option.metadata.entityLevelString),
        ) as DocumentEntityType,
      },
    }));
    return service;
  }

  private getDocumentTypeOptionById(documentTypeId: string | null) {
    return this.documentTypeOptions.find((docType) => docType.value.toString() === documentTypeId?.toString());
  }

  public getDocumentEntityOptions({
    documentTypeId,
    claimSetNumber,
    claimSetId,
    claims,
    claimants,
  }: DocumentEntityOptionsProps): SelectOption[] {
    // TODO: remove this mapping when backend will provide api that returns all claimsets/claims/claimants/... in the claimset
    // in the form that can be used on the entity selection view of the document creation form (labels should be formed on the backend side).
    const relatedEDSEntityCodeToOptions = {
      [DocumentEntityType.ClaimSet]: [{ key: claimSetId.toString(), value: claimSetNumber }],
      [DocumentEntityType.Claim]: [...claims],
      [DocumentEntityType.Claimant]: [...claimants],
    };
    const documentTypeDescriptor = this.getDocumentTypeOptionById(documentTypeId);
    const documentRelatedEntityOptions =
      relatedEDSEntityCodeToOptions[documentTypeDescriptor?.metadata.entityLevelString || ''];

    return (
      documentRelatedEntityOptions?.sort(({ value: valueLeft }, { value: valueRight }) =>
        valueLeft.toLowerCase() > valueRight.toLowerCase() ? 1 : -1,
      ) || []
    );
  }

  public getDocumentEntityType(documentTypeId: string | null) {
    const documentTypeOption = this.getDocumentTypeOptionById(documentTypeId);
    return documentTypeOption?.metadata.entityLevelString || null;
  }

  public getEntityDocumentType(entityType: DocumentEntityType) {
    return (
      this.documentTypeOptions.find((docType) => docType.metadata.entityLevelString === entityType)?.label.toString() ||
      ''
    );
  }
}
